import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import {AUTH_INFO_AREA} from '../../actions/authActions';
import {postMessage, setPostSuspended} from '../../frame/FrameActions';
import {Frame} from '../../frame/index';
import {storeFromArea, StoreUtils} from '../../utils/index';
import {commonDefaultProps, commonPropTypes, filterPropType} from '../common/props';
import {buildMapioUrl} from '../common/utils';

const {objectEquals} = StoreUtils;

/**
 * Názvy properties obsahující stavovou informaci mapio.
 */
const stateProps = [
    'topic',
    'backgroundLayer',
    'foregroundLayer',
    'layers',
    'catalogNodes',
    'location',
    'zoom',
    'filter',
];

/**
 * Vrátí z properties pouze ty, které představují stavovou informaci mapio.
 *
 * @param properties
 * @returns stavové properties
 */
export function getStateProps(props) {
    const result = {};
    for (const name of stateProps) {
        const value = props[name];
        if (value !== undefined) {
            result[name] = value;
        }
    }
    return result;
}

/**
 * Vrátí z properties pouze ty, které nepředstavují stavovou informaci mapio.
 *
 * @param properties
 * @returns nestavové properties
 */
export function removeStateProps(props) {
    const result = {...props};
    for (const name of stateProps) {
        delete result[name];
    }
    return result;
}

// Poslední vygenerované ID instance mapio
let lastInstanceId = 0;

/**
 * Předek pro mapové komponenty.
 */
export default class AbstractMap extends PureComponent {

    static propTypes = {
        ...commonPropTypes,
        auth: PropTypes.object,
        topic: PropTypes.string,
        backgroundLayer: PropTypes.string,
        foregroundLayer: PropTypes.string,
        layers: PropTypes.arrayOf(PropTypes.string),
        catalogNodes: PropTypes.arrayOf(PropTypes.string),
        location: PropTypes.arrayOf(PropTypes.number),
        zoom: PropTypes.number,
        filter: filterPropType,
        onTopicChange: PropTypes.func,
        onBackgroundLayerChange: PropTypes.func,
        onForegroundLayerChange: PropTypes.func,
        onLayersChange: PropTypes.func,
        onCatalogNodesChange: PropTypes.func,
        onLocationChange: PropTypes.func,
        onZoomChange: PropTypes.func,
        onFilterChange: PropTypes.func,
    };

    static defaultProps = {
        ...commonDefaultProps
        // Všechny synchronizované properties necháváme zcela záměrně undefined, aby ve výchozím stavu nedocházelo
        // k jejich synchronizaci a použila se výchozí hodnota definovaná konfigurací na straně mapio.
    };

    static initialSyncedProps = [
        'dateFilterItems'
    ];

    static syncedProps = [
        ...stateProps,
        'dateFilterItems'
    ];

    instanceId = String(++lastInstanceId); // Unikátní ID instance mapio
    ready = false; // Zda je stránka v iframe připravena ke komunikaci
    mapioProps = {}; // Stav synchronizovaných props na straně mapio
    lastSrc = null; // Poslední vygenerovaná hodnota src atributu iframe
    lastPath = null; // Poslední hodnota path, použitá pro vygenerování src atributu iframe

    constructor(props) {
        super(props);
        this.suspendPost();
        this.postInitialSyncedProps(); // Zprávy musíme poslat už teď, aby byly na začátku fronty
    }

    componentWillReceiveProps(newProps) {
        const {props} = this;
        for (const name of this.getSyncedProps()) {
            const newValue = newProps[name];
            if (newValue !== undefined) { // Synchronizujeme jen property s definovanou hodnotou
                if (!objectEquals(props[name], newValue)) {
                    this.postProperty(name, newValue);
                }
            }
        }
    }

    suspendPost() {
        const {dispatch, area} = this.props;
        dispatch(setPostSuspended(area, true));
    }

    resumePost() {
        const {dispatch, area} = this.props;
        dispatch(setPostSuspended(area, false));
    }

    getInitialSyncedProps() {
        return AbstractMap.initialSyncedProps;
    }

    getSyncedProps() {
        return AbstractMap.syncedProps;
    }

    postInitialSyncedProps() {
        const {props} = this;
        for (const name of this.getInitialSyncedProps()) {
            const value = props[name];
            if (value !== undefined) { // Synchronizujeme jen property s definovanou hodnotou
                this.postProperty(name, value);
            }
        }
        this.componentPostedInitialSyncedProps();
    }

    postProperty(name, value) {
        // Hodnotu v mapioProps zcela záměrně aktualizujeme až v handlePropertyChange
        if (!objectEquals(this.mapioProps[name], value)) {
            const {dispatch, area} = this.props;
            dispatch(postMessage(area, {type: 'PROPERTY_SET', name, value}));
        }
    }

    componentPostedInitialSyncedProps() {
    }

    handleReady() {
        if (this.ready) {
            console.warn('Unexpected reload of iframe content');
        } else {
            // Až teď můžeme začít komunikovat a poslat výchozí konfiguraci
            this.resumePost();
            this.ready = true;
        }
    }

    handlePropertyChange(name, value) {
        this.mapioProps[name] = value;

        const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
        const callbackName = `on${capitalizedName}Change`;
        const callback = this.props[callbackName];

        if (callback) {
            callback(value);
        }
    }

    handleEvent(event) {
        const {onEvent} = this.props;
        if (onEvent) {
            onEvent(event);
        }
    }

    handleMessage = message => {
        if (!message || message.sender !== 'mapio' || message.instanceId !== this.instanceId) {
            return; // Zpráva od jiné aplikace nebo jiné instance mapio
        }

        if (__DEV__) {
            console.log('App received message from Mapio', message);
        }

        switch (message.type) {
            case 'READY':
                this.handleReady();
                break;

            case 'PROPERTY_CHANGE':
                this.handlePropertyChange(message.name, message.value);
                break;

            case 'EVENT':
                this.handleEvent(message.event);
                break;

            default:
                console.warn('Unrecognized message', message);
                break;
        }
    };

    getFrameSrc(path) {
        // Hodnotu src přegenerováváme jen při:
        //
        // 1) Prvotní inicializaci.
        // 2) Při změně cesty v URL (jiná mapio stránka, vyžaduje reload).
        //
        // Změnu query parametrů URL ignorujeme. Používají se pouze pro prvotní inicializaci
        // a navíc by změna URL způsobovala nechtěný reload stránky v iframe.

        if (this.lastSrc == null || this.lastPath !== path) {
            this.lastPath = path;
            this.lastSrc = this.buildFrameSrc(path);
        }

        return this.lastSrc;
    }

    buildFrameSrc(path) {
        const {serverUrl, queryParams} = this.props;
        if (!serverUrl) {
            return null;
        }

        const {instanceId} = this;
        const contextParams = this.getContextParamsWithAuth();
        const stateProps = getStateProps(this.props);

        return buildMapioUrl(serverUrl, {path, queryParams, contextParams, instanceId, ...stateProps});
    }

    getContextParamsWithAuth() {
        const {auth, contextParams} = this.props;
        if (auth && auth.data) {
            const login = auth.data.loginEncoded;
            return contextParams ? {...contextParams, login} : {login};
        }
        return contextParams;
    }

    renderFrame(props) {
        const {
            auth,
            dispatch,
            serverUrl,
            queryParams,
            contextParams,
            topic,
            backgroundLayer,
            foregroundLayer,
            layers,
            catalogNodes,
            location,
            zoom,
            filter,
            dateFilterItems,
            onTopicChange,
            onBackgroundLayerChange,
            onForegroundLayerChange,
            onLayersChange,
            onCatalogNodesChange,
            onLocationChange,
            onZoomChange,
            onFilterChange,
            onEvent,
            ...frameProps
        } = props;

        return <Frame onMessage={this.handleMessage} {...frameProps}/>;
    }

}

/**
 * Napojení do redux store pro potomky AbstractMap.
 */
export const connectAbstractMap = connect(state => ({
    auth: storeFromArea(state, AUTH_INFO_AREA, false)
}));
